import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useFacturationState } from '../hooks/bank/useFacturationState';
import { useFacturationData } from '../hooks/bank/useFacturationData';
import { useFacturationHandlers } from '../hooks/bank/useFacturationHandlers';
import { useAuth } from '../contexts/AuthContext';
import { useYear } from '../contexts/YearContext';
import { colors } from '../styles/colors';
import { X, AlertTriangle, Plus } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { STATUTS as STATUTS_LIST } from '../utils/sortFactures';
import { getExterneFilters, getInterneFilters } from '../config/facturationFilters';
import FilterBar from './FilterBar';
import ActionButton from './ActionButton';
import SortableHeader from './facturation/SortableHeader';
import FactureRow from './facturation/FactureRow';
import FacturationComparison from './facturation/FacturationComparison';
import FacturationModals from './facturation/FacturationModals';
import AnimatedContent from './AnimatedContent';

const STATUTS = STATUTS_LIST;

/**
 * Calcule l'année par défaut (année en cours ou précédente si janvier)
 */
const getDefaultYear = () => {
  const today = new Date();
  const currentMonth = today.getMonth();
  return currentMonth === 0 ? today.getFullYear() - 1 : today.getFullYear();
};

/**
 * FacturationTable - Composant principal de gestion des factures
 *
 * Refactorisé pour utiliser les hooks extraits:
 * - useFacturationState: gestion de l'état et des modales
 * - useFacturationData: chargement et transformation des données
 * - useFacturationHandlers: tous les handlers d'actions
 * - FacturationModals: composant regroupant les 11 modales
 */
export default function FacturationTable({ initialTab = 'externe' }) {
  const location = useLocation();
  const { hasPermission, ACTIONS: AUTH_ACTIONS, RESOURCES } = useAuth();
  const { showToast } = useToast();

  // Permissions
  const canEditFacturation = hasPermission(AUTH_ACTIONS.WRITE, RESOURCES.FACTURATION);
  const canEditFacturationStatus = hasPermission(AUTH_ACTIONS.WRITE, RESOURCES.FACTURATION_STATUS);

  // État centralisé
  const { state, dispatch, ACTIONS } = useFacturationState(getDefaultYear(), initialTab);

  // Contexte global pour l'année
  const { selectedYear: filterAnnee, setSelectedYear: setFilterAnnee, setAvailableYears } = useYear();

  // Données via hook extrait
  const {
    prestatairesMap,
    consultantsMap,
    anneesDisponibles,
    caNetTotal,
    caBrut,
    totalFactureInterne,
    totalFactureExterne,
    facturationDetailData,
    prestatairesDisponibles,
    consultantsDisponibles,
    facturesFiltrees,
    loadData
  } = useFacturationData({ state, dispatch, ACTIONS, filterAnnee });

  // État pour les modales locales
  const [showFacturationDetailModal, setShowFacturationDetailModal] = useState(false);
  const [progressModal, setProgressModal] = useState({
    isOpen: false,
    title: '',
    steps: [],
    errorMessage: null
  });
  const [statusChangeConfirmation, setStatusChangeConfirmation] = useState({
    isOpen: false,
    newStatut: null,
    loading: false
  });

  // Déstructurer l'état
  const {
    activeTab,
    prestataires,
    consultants,
    loading,
    error,
    filterMois,
    filterTrimestre,
    filterStatut,
    filterPrestataire,
    filterConsultant,
    hidePaid,
    sortColumn,
    sortDirection,
    facturesExternes,
    facturesInternes
  } = state;

  // Setters simplifiés
  const setActiveTab = useCallback((v) => dispatch({ type: ACTIONS.SET_ACTIVE_TAB, payload: v }), [dispatch, ACTIONS]);
  const setFacturesExternes = useCallback((v) => dispatch({ type: ACTIONS.SET_FACTURES_EXTERNES, payload: typeof v === 'function' ? v(facturesExternes) : v }), [dispatch, ACTIONS, facturesExternes]);
  const setFacturesInternes = useCallback((v) => dispatch({ type: ACTIONS.SET_FACTURES_INTERNES, payload: typeof v === 'function' ? v(facturesInternes) : v }), [dispatch, ACTIONS, facturesInternes]);
  const setError = useCallback((v) => dispatch({ type: ACTIONS.SET_ERROR, payload: v }), [dispatch, ACTIONS]);
  const setFilterMois = useCallback((v) => dispatch({ type: ACTIONS.SET_FILTER_MOIS, payload: v }), [dispatch, ACTIONS]);
  const setFilterTrimestre = useCallback((v) => dispatch({ type: ACTIONS.SET_FILTER_TRIMESTRE, payload: v }), [dispatch, ACTIONS]);
  const setFilterStatut = useCallback((v) => dispatch({ type: ACTIONS.SET_FILTER_STATUT, payload: v }), [dispatch, ACTIONS]);
  const setFilterPrestataire = useCallback((v) => dispatch({ type: ACTIONS.SET_FILTER_PRESTATAIRE, payload: v }), [dispatch, ACTIONS]);
  const setFilterConsultant = useCallback((v) => dispatch({ type: ACTIONS.SET_FILTER_CONSULTANT, payload: v }), [dispatch, ACTIONS]);
  const setHidePaid = useCallback((v) => dispatch({ type: ACTIONS.SET_HIDE_PAID, payload: v }), [dispatch, ACTIONS]);
  const setSelectedPaymentFacture = useCallback((v) => v ? dispatch({ type: ACTIONS.OPEN_PAYMENT_MODAL, payload: v }) : dispatch({ type: ACTIONS.CLOSE_PAYMENT_MODAL }), [dispatch, ACTIONS]);
  const setSelectedPaymentFactureInterne = useCallback((v) => v ? dispatch({ type: ACTIONS.OPEN_PAYMENT_HISTORY_MODAL, payload: v }) : dispatch({ type: ACTIONS.CLOSE_PAYMENT_HISTORY_MODAL }), [dispatch, ACTIONS]);

  // Handlers via hook extrait
  const handlers = useFacturationHandlers({
    state,
    dispatch,
    ACTIONS,
    loadData,
    showToast,
    prestataires,
    consultantsMap,
    setProgressModal,
    setFacturesExternes,
    setFacturesInternes,
    setError
  });

  // === EFFETS ===

  // Appliquer les filtres depuis l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statutsFromUrl = params.get('statuts');
    const anneeFromUrl = params.get('annee');

    if (statutsFromUrl) setFilterStatut(statutsFromUrl.split(','));
    if (anneeFromUrl !== null) {
      const anneeNum = parseInt(anneeFromUrl, 10);
      if (!isNaN(anneeNum)) setFilterAnnee(anneeNum);
    }
  }, [location.search, setFilterAnnee, setFilterStatut]);

  // Synchroniser activeTab avec initialTab
  useEffect(() => {
    if (initialTab !== activeTab) setActiveTab(initialTab);
  }, [initialTab, activeTab, setActiveTab]);

  // Tri par défaut selon l'onglet
  useEffect(() => {
    dispatch({ type: ACTIONS.SET_SORT_COLUMN, payload: activeTab === 'interne' ? 'periode' : 'numero' });
    dispatch({ type: ACTIONS.SET_SORT_DIRECTION, payload: 'desc' });
  }, [activeTab, dispatch, ACTIONS]);

  // Synchroniser les années disponibles
  useEffect(() => {
    if (anneesDisponibles.length > 0) setAvailableYears(anneesDisponibles);
  }, [anneesDisponibles, setAvailableYears]);

  // === RENDER ===
  return (
    <div style={{ display: 'flex', flexDirection: 'column', fontFamily: '"Noto Sans", sans-serif' }}>
      {/* Erreur */}
      {error && (
        <div style={{
          padding: '1rem', borderRadius: '0.5rem',
          backgroundColor: colors.background.lightRed,
          border: `1px solid ${colors.status.error}`,
          display: 'flex', alignItems: 'center', gap: '0.75rem'
        }}>
          <AlertTriangle size={20} color={colors.hover.errorDark} />
          <span style={{ color: colors.text.darkRed, fontSize: '0.875rem' }}>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={16} color={colors.text.darkRed} />
          </button>
        </div>
      )}

      {/* Filtres */}
      <FilterBar
        filters={activeTab === 'externe'
          ? getExterneFilters({ anneesDisponibles, filterAnnee, setFilterAnnee, filterMois, setFilterMois, filterPrestataire, setFilterPrestataire, prestatairesDisponibles, filterStatut, setFilterStatut, STATUTS, hidePaid, setHidePaid })
          : getInterneFilters({ anneesDisponibles, filterAnnee, setFilterAnnee, filterTrimestre, setFilterTrimestre, filterConsultant, setFilterConsultant, consultantsDisponibles, filterStatut, setFilterStatut, STATUTS, hidePaid, setHidePaid })
        }
        resultCount={`${facturesFiltrees.length} facture${facturesFiltrees.length > 1 ? 's' : ''}`}
        actionButtons={canEditFacturation && (
          <ActionButton
            icon={Plus}
            text={activeTab === 'externe' ? 'Facture Externe' : 'Facture Interne'}
            onClick={() => dispatch({ type: ACTIONS.OPEN_AJOUTER_MODAL, payload: activeTab })}
            variant={activeTab === 'externe' ? 'primary' : 'secondary'}
            iconSize={18}
          />
        )}
      />

      <AnimatedContent>
      {/* Comparaison CA vs Facturé */}
      <div style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.35s ease-out' }}>
        <FacturationComparison
          caAnnuel={activeTab === 'interne' ? (caNetTotal?.ANNUEL || 0) : (caBrut?.ANNUEL || 0)}
          totalFacture={activeTab === 'interne' ? totalFactureInterne : totalFactureExterne}
          onShowDetail={() => setShowFacturationDetailModal(true)}
        />
      </div>

      {/* Tableau des factures */}
      <div style={{ backgroundColor: colors.text.white, borderRadius: '0.75rem', border: `2px solid ${colors.primary.teal}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: colors.primary.teal }}>
            <tr style={{ backgroundColor: colors.primary.teal, color: colors.text.white }}>
              <SortableHeader column="numero" label="N° Facture" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="left" />
              <SortableHeader column="periode" label={activeTab === 'externe' ? 'Période' : 'Trimestre'} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="left" />
              <SortableHeader column="tiers" label={activeTab === 'externe' ? 'Prestataire' : 'Consultant'} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="left" />
              {activeTab === 'interne' && <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600' }}>Client</th>}
              <SortableHeader column="montant" label="Montant (HT)" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="right" />
              <SortableHeader column="tauxtva" label="TVA (%)" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="right" />
              <SortableHeader column="tva" label="TVA (€)" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="right" />
              <SortableHeader column="montantTTC" label="Montant TTC" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="right" />
              {activeTab === 'interne' && <SortableHeader column="montantPaye" label="Montant payé" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="right" />}
              {activeTab === 'interne' && <SortableHeader column="resteAPayer" label="Reste à payer" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="right" />}
              <SortableHeader column="dateEmission" label="Date émission" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="center" />
              <SortableHeader column="statut" label="Statut" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="center" />
              <SortableHeader column="datePaiement" label="Date paiement" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handlers.handleSort} align="center" />
              <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: '600' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={activeTab === 'interne' ? 14 : 11} style={{ padding: '3rem', textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: '48px', height: '48px', border: `4px solid ${colors.secondary.lightTeal}`, borderTop: `4px solid ${colors.primary.teal}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                  </div>
                </td>
              </tr>
            ) : facturesFiltrees.length === 0 ? (
              <tr>
                <td colSpan={activeTab === 'interne' ? 14 : 11} style={{ padding: '2rem', textAlign: 'center', color: colors.secondary.mediumGray }}>
                  Aucune facture trouvée
                </td>
              </tr>
            ) : (
              facturesFiltrees.map((facture, index) => (
                <FactureRow
                  key={facture.id}
                  facture={facture}
                  index={index}
                  type={activeTab}
                  handlers={{
                    onOpenDetail: handlers.openDetail,
                    onSendEmail: handlers.handleSendEmail,
                    onViewPdf: handlers.handleViewPdfFromDrive,
                    onMarkAsPaid: (f) => setSelectedPaymentFacture(f),
                    onEditInterne: handlers.handleEditFactureInterne,
                    onDownloadInterne: handlers.handleDownloadFactureInterne,
                    onPaymentHistory: (f) => setSelectedPaymentFactureInterne(f),
                    onStatutChange: handlers.handleStatutChange,
                    onDatePaiementChange: handlers.handleDatePaiementChange
                  }}
                  permissions={{ canEditFacturation, canEditFacturationStatus }}
                  maps={{ prestataires: prestatairesMap, consultants: consultantsMap }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
      </AnimatedContent>

      {/* Modales */}
      <FacturationModals
        state={state}
        dispatch={dispatch}
        ACTIONS={ACTIONS}
        handlers={handlers}
        data={{ prestataires, consultants, facturationDetailData }}
        modalsState={{
          progressModal,
          setProgressModal,
          statusChangeConfirmation,
          setStatusChangeConfirmation,
          showFacturationDetailModal,
          setShowFacturationDetailModal
        }}
        loadData={loadData}
        showToast={showToast}
      />
    </div>
  );
}
